import React, { useEffect, useRef, useState } from "react";
import { Aurora, Card, PageHeader, Logo, Stepper } from "./Shell";
import Instructions from "./Instructions";
import { useDemo } from "./DemoContext";

const STEPS = [
  { n: 5, label: "things you can SEE" },
  { n: 4, label: "things you can TOUCH" },
  { n: 3, label: "things you can HEAR" },
  { n: 2, label: "things you can SMELL" },
  { n: 1, label: "thing you can TASTE" },
];

// Static "why grounding matters" guidance — extracted so we don't allocate
// a new array on every render of the grounding exit screen.
const WHY_GROUNDING_MATTERS = [
  { strong: "Don't skip this step", text: "grounding is what closes the bilateral session safely and prevents lingering activation." },
  { strong: "Move slowly", text: "speak each item out loud or in your head. Touch what you can. This anchors attention back to the present." },
  { strong: "5 SEE → 4 TOUCH → 3 HEAR → 2 SMELL → 1 TASTE", text: "the order is intentional. It walks attention from the most external sense (sight) inward toward the body." },
  { strong: "Approximate is fine", text: "if you can't find a smell, name one you remember. The point is the ritual, not perfection." },
];

export default function GroundingExit({ onDone, stepIndex, steps }) {
  const [current, setCurrent] = useState(0);
  const [completed, setCompleted] = useState(Array(5).fill(false));
  const [entries, setEntries] = useState(["", "", "", "", ""]);

  const allDone = completed.every(Boolean);

  const next = () => {
    if (current < STEPS.length - 1) setCurrent(current + 1);
  };

  const mark = (idx) => {
    const c = [...completed];
    c[idx] = true;
    setCompleted(c);
  };

  // Demo auto-fill: stagger-mark each of the 5 grounding steps, then finish.
  // No cleanup — see DemoContext for the Strict-Mode rationale.
  const demo = useDemo();
  const demoRanRef = useRef(false);
  useEffect(() => {
    if (!demo.active || demoRanRef.current) return;
    demoRanRef.current = true;
    const sample = [
      "the lamp, my hand, the window",
      "the desk, my shirt, the floor",
      "a hum, footsteps, my breath",
      "coffee, fresh air",
      "mint",
    ];
    setEntries(sample);
    sample.forEach((_, i) => {
      setTimeout(() => {
        if (!demo.active) return;
        setCompleted((prev) => {
          const next = [...prev];
          next[i] = true;
          return next;
        });
        setCurrent(Math.min(i + 1, 4));
      }, 700 * (i + 1));
    });
    setTimeout(() => {
      if (demo.active) {
        onDone({
          grounding_completed: true,
          grounding_notes: sample.join(" | "),
        });
      }
    }, 700 * 6);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demo.active]);

  return (
    <Aurora>
      <div className="relative max-w-3xl mx-auto px-6 sm:px-10 py-10">
        <div className="flex items-center justify-between mb-12">
          <Logo size="md" />
          <Stepper steps={steps} current={stepIndex} />
        </div>

        <PageHeader
          eyebrow="Step 07 · Grounding Exit (5-4-3-2-1)"
          title="Return to the room."
          subtitle="A brief sensory grounding ritual seals the session. Move at your own pace — there is no rush."
        />

        <Instructions title="Why grounding matters" items={WHY_GROUNDING_MATTERS} />

        <div className="space-y-4 fade-up">
          {STEPS.map((s, i) => (
            <Card
              key={`grounding-${s.n}-${i}`}
              className={`!p-6 transition-all ${
                i === current ? "border-[color:var(--accent-blue)]" : ""
              }`}
            >
              <div className="flex items-center gap-6">
                <div
                  className={`serif text-6xl w-16 text-center font-bold ${
                    completed[i] ? "text-blue" : "text-ink"
                  }`}
                >
                  {s.n}
                </div>
                <div className="flex-1">
                  <div className="eyebrow mb-1" style={{ color: "var(--ink-mute)" }}>
                    Step {i + 1} of 5
                  </div>
                  <div className="text-ink text-xl leading-snug">
                    Name {s.n} {s.label}.
                  </div>
                  <input
                    type="text"
                    placeholder={`e.g. ${
                      i === 0
                        ? "the lamp, my hand, …"
                        : i === 1
                        ? "the desk, my shirt, …"
                        : i === 2
                        ? "a hum, footsteps, …"
                        : i === 3
                        ? "coffee, …"
                        : "mint…"
                    }`}
                    value={entries[i]}
                    onChange={(e) => {
                      const next = [...entries];
                      next[i] = e.target.value;
                      setEntries(next);
                    }}
                    className="mt-3 w-full bg-[color:var(--bg-tint)] border border-[color:var(--hairline)] focus:border-[color:var(--accent-blue)] outline-none rounded-xl px-4 py-2.5 text-base text-ink placeholder:text-soft"
                    data-testid={`grounding-step-${i}-input`}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => {
                    mark(i);
                    if (i === current) next();
                  }}
                  className={`px-5 py-2.5 rounded-full text-sm tracking-widest border transition-all font-semibold ${
                    completed[i]
                      ? "bg-[color:var(--accent-blue-soft)] border-[color:var(--accent-blue)] text-blue"
                      : "border-[color:var(--hairline)] text-soft hover:border-[color:var(--accent-blue)] hover:text-ink"
                  }`}
                  data-testid={`grounding-step-${i}-done`}
                >
                  {completed[i] ? "✓ DONE" : "MARK"}
                </button>
              </div>
            </Card>
          ))}
        </div>

        <div className="mt-10 flex items-center gap-4">
          <button
            type="button"
            className="btn-primary"
            onClick={() =>
              onDone({
                grounding_completed: allDone,
                grounding_notes: entries.join(" | "),
              })
            }
            disabled={!allDone}
            data-testid="grounding-finish-btn"
          >
            {allDone ? "Complete & Save Session →" : "Complete all 5 steps to continue"}
          </button>
        </div>
      </div>
    </Aurora>
  );
}
